import { descendants, parseXml, WORD_NS, XmlFault } from './xml';

interface Token { start: number; end: number; raw: string; name: string; kind: 'open' | 'close' | 'empty' | 'other' }
interface Element extends Token { children: Element[]; parent?: Element; closeStart: number; closeEnd: number; close: string }
function revisionId(raw: string): { value: number; start: number; end: number; valueStart: number; valueEnd: number } | null {
  // Consume complete quoted attributes: a lawyer's name can itself contain
  // the characters "w:id" and must never be mistaken for the revision ID.
  for (const match of raw.matchAll(/\s+([A-Za-z_][\w:.-]*)\s*=\s*("[^"]*"|'[^']*')/g)) {
    if (match[1] !== 'w:id') continue;
    const quoted = match[2];
    if (!quoted || !/^\d+$/.test(quoted.slice(1, -1))) return null;
    const value = Number(quoted.slice(1, -1));
    if (!Number.isSafeInteger(value)) return null;
    const end = match.index + match[0].length;
    return { value, start: match.index, end, valueStart: end - quoted.length + 1, valueEnd: end - 1 };
  }
  return null;
}

function withoutRevisionId(raw: string): string {
  const id = revisionId(raw);
  return id ? raw.slice(0, id.start) + raw.slice(id.end) : raw;
}

export function tokenize(xml: string): Token[] {
  const pattern = /<!--[^]*?-->|<!\[CDATA\[[^]*?\]\]>|<\?[^]*?\?>|<\/?[A-Za-z_][\w:.-]*(?:[^<>"']|"[^"]*"|'[^']*')*>/g;
  return [...xml.matchAll(pattern)].map((match) => {
    const raw = match[0];
    const name = /^<\/?([A-Za-z_][\w:.-]*)/.exec(raw)?.[1] ?? '';
    return { start: match.index, end: match.index + raw.length, raw, name,
      kind: !name ? 'other' : raw.startsWith('</') ? 'close' : raw.endsWith('/>') ? 'empty' : 'open' };
  });
}

function refuse(): never { throw new XmlFault('DOCX_TRACKED_LINK_UNSUPPORTED'); }

/** Independent intervals still describe meaning when the XML tags cross. */
function ledger(xml: string): string {
  const deletions: string[] = [];
  const links: string[] = [];
  const entries: unknown[] = [];
  let cursor = 0;
  for (const token of tokenize(xml)) {
    const text = xml.slice(cursor, token.start);
    if (text.trim()) entries.push([text, [...deletions], [...links]]);
    const active = token.name === 'w:del' ? deletions : token.name === 'w:hyperlink' ? links : null;
    if (active && token.kind === 'open') active.push(token.name === 'w:del' ? withoutRevisionId(token.raw) : token.raw);
    if (active && token.kind === 'close' && active.pop() === undefined) refuse();
    cursor = token.end;
  }
  if (xml.slice(cursor).trim()) entries.push([xml.slice(cursor), [...deletions], [...links]]);
  if (deletions.length || links.length) refuse();
  return JSON.stringify(entries);
}

function withoutDeletionTags(xml: string): string {
  let result = ''; let cursor = 0;
  for (const token of tokenize(xml)) {
    if (token.name !== 'w:del') continue;
    result += xml.slice(cursor, token.start); cursor = token.end;
  }
  return result + xml.slice(cursor);
}

function elements(xml: string): Element[] {
  const result: Element[] = []; const stack: Element[] = [];
  for (const token of tokenize(xml)) {
    if (token.kind === 'other') continue;
    if (token.kind !== 'close') {
      const parent = stack.at(-1);
      const element: Element = { ...token, parent, children: [], closeStart: token.end, closeEnd: token.end, close: '' };
      parent?.children.push(element); result.push(element);
      if (token.kind === 'open') stack.push(element);
    } else {
      const element = stack.pop();
      if (!element || element.name !== token.name) refuse();
      element.closeStart = token.start; element.closeEnd = token.end; element.close = token.raw;
    }
  }
  if (stack.length) refuse();
  return result;
}

function uncross(source: string): string {
  let xml = source;
  for (let pass = 0; pass < 128; pass++) {
    const tokens = tokenize(xml); const stack: Token[] = [];
    let changed = false;
    for (let index = 0; index < tokens.length; index++) {
      const token = tokens[index];
      if (!token) continue;
      if (token.kind === 'open') stack.push(token);
      if (token.kind !== 'close') continue;
      const top = stack.at(-1); const below = stack.at(-2);
      if (top?.name === token.name) { stack.pop(); continue; }
      if (!top || !below || below.name !== token.name
        || new Set([top.name, below.name]).size !== 2
        || ![top.name, below.name].every((name) => name === 'w:del' || name === 'w:hyperlink')) refuse();
      if (token.name === 'w:del') {
        const next = tokens[index + 1];
        if (!next || next.kind !== 'close' || next.name !== 'w:hyperlink' || xml.slice(token.end, next.start).trim()) refuse();
        xml = xml.slice(0, token.start) + next.raw + xml.slice(token.end, next.start) + token.raw + xml.slice(next.end);
      } else {
        if (xml.slice(below.end, top.start).trim()) refuse();
        xml = xml.slice(0, below.start) + top.raw + xml.slice(below.end, top.start) + below.raw + xml.slice(top.end);
      }
      changed = true; break;
    }
    if (!changed) { parseXml(xml); return xml; }
  }
  return refuse();
}

/**
 * Split only the affected deletion around its hyperlink. Moving closing tags
 * alone makes XML parse but hides the deleted link in Word/LibreOffice (#1516).
 * No revision is accepted/rejected. Only revision span IDs may be allocated.
 * Sources (verified 2026-09-16):
 * https://learn.microsoft.com/en-us/dotnet/api/documentformat.openxml.wordprocessing.hyperlink
 * https://learn.microsoft.com/en-us/dotnet/api/documentformat.openxml.wordprocessing.deletedrun
 */
export function repairTrackedLinks(source: string): { xml: string; count: number } {
  const beforeLedger = ledger(source);
  let xml = uncross(source);
  const parsed = parseXml(xml);
  for (const node of descendants(parsed)) {
    if ((node.name === 'w:del' || node.name === 'w:hyperlink') && node.uri !== WORD_NS) refuse();
  }
  const nodes = elements(xml);
  let allocated = 0;
  for (const token of tokenize(xml)) {
    const id = revisionId(token.raw);
    if (id) allocated = Math.max(allocated, id.value);
  }
  const replacements: { start: number; end: number; text: string }[] = [];
  for (const deletion of nodes.filter((node) => node.name === 'w:del' && node.children.some((child) => child.name === 'w:hyperlink'))) {
    for (let parent = deletion.parent; parent; parent = parent.parent) if (parent.name === 'w:del') refuse();
    const sourceId = revisionId(deletion.raw);
    if (!deletion.close || !sourceId) refuse();
    let wrapped = false;
    const wrap = (fragment: string): string => {
      if (!fragment.trim()) return fragment;
      let opening = deletion.raw;
      if (wrapped) {
        allocated++;
        if (!Number.isSafeInteger(allocated) || allocated > 2_147_483_647) refuse();
        opening = opening.slice(0, sourceId.valueStart) + String(allocated) + opening.slice(sourceId.valueEnd);
      }
      wrapped = true;
      return opening + fragment + deletion.close;
    };
    let cursor = deletion.end; let replacement = '';
    for (const child of deletion.children.filter((node) => node.name === 'w:hyperlink')) {
      const inner = xml.slice(child.end, child.closeStart);
      if (tokenize(inner).some((token) => ['w:del', 'w:ins', 'w:hyperlink'].includes(token.name))) refuse();
      replacement += wrap(xml.slice(cursor, child.start)) + child.raw + wrap(inner) + child.close;
      cursor = child.closeEnd;
    }
    replacement += wrap(xml.slice(cursor, deletion.closeStart));
    replacements.push({ start: deletion.start, end: deletion.closeEnd, text: replacement });
  }
  for (const replacement of replacements.sort((a, b) => b.start - a.start)) {
    xml = xml.slice(0, replacement.start) + replacement.text + xml.slice(replacement.end);
  }
  parseXml(xml);
  if (beforeLedger !== ledger(xml) || withoutDeletionTags(source) !== withoutDeletionTags(xml)) refuse();
  return { xml, count: replacements.length };
}
